/**
 * O "vinculando…" depois do "Definir a empresa" (13.20b2, herança do code review da 13.20c).
 *
 * **A tela só define.** O `PUT /negocios/{id}/definicao-da-empresa` grava a Empresa e publica o
 * evento; o contato do Negócio é vinculado a ela pelo `cadastro`, ao consumir esse evento, alguns
 * segundos depois (`vinculo_pela_definicao.py`). Não há rota de usuário para esse vínculo, e a tela
 * não pede nenhum: ela **espera** a réplica alcançar, relendo a Pessoa no lote `GET /pessoas?ids=`
 * até a Empresa definida aparecer nas `empresa_ids` dela.
 *
 * - **vinculando**: a Pessoa ainda não veio no lote (a Responsável que lia o contato só pelo
 *   Negócio sem Empresa deixa de lê-lo assim que o Negócio tem Empresa, e volta a lê-lo pela base
 *   quando o vínculo chega) ou veio sem a Empresa;
 * - **vinculado**: a Empresa está nas `empresa_ids` — as listas de Pessoas são relidas;
 * - **demorando**: a espera tem teto (`ESPERA_DO_VINCULO`). O vínculo que não chega é, hoje, o da
 *   Empresa que já tem outra Pessoa com o mesmo telefone (DW-586, a decidir com o Gustavo): a tela
 *   não afirma a causa, diz que o contato ainda não aparece na Empresa e oferece "Conferir de novo".
 *
 * A leitura tem chave própria debaixo de `['cadastro', 'pessoas']` — o prefixo que a edição e o
 * vínculo invalidam —, e não a do lote dos nomes (que guarda cinco minutos).
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

import { estaNaEmpresa, lerPessoasPorIds } from '@/modulos/cadastro/api'

/** O contato a caminho da Empresa definida — o estado do "vinculando…". */
export const VINCULANDO_O_CONTATO = 'Vinculando o contato à empresa…'

/** O contato chegou à Empresa definida. */
export const CONTATO_VINCULADO_A_EMPRESA = 'Contato vinculado à empresa.'

/**
 * O teto da espera passou e o contato não apareceu na Empresa. A frase não afirma a causa (a DW-586
 * está aberta): diz o que se vê e o que fazer.
 */
export const CONTATO_AINDA_NAO_VINCULADO =
  'O contato ainda não aparece nesta empresa. Confira de novo em instantes ou abra a ficha da empresa.'

/** O caminho do "demorando" no painel: a ficha da Empresa definida (code review, B4). */
export const ABRIR_A_FICHA_DA_EMPRESA = 'Abrir a ficha da empresa'

/** A saída do "demorando": recomeça a espera. */
export const CONFERIR_DE_NOVO = 'Conferir de novo'

/**
 * O ritmo e o teto da espera: a cada `intervaloEmMs`, até `tentativas` leituras (30 s no padrão).
 * Um objeto do módulo, e não constantes soltas, para os testes o encurtarem.
 */
export const ESPERA_DO_VINCULO = { intervaloEmMs: 2000, tentativas: 15 }

export type EstadoDoVinculo = 'vinculando' | 'vinculado' | 'demorando'

export const chaveDaEsperaDoVinculo = (pessoaId: string, empresaId: string) =>
  ['cadastro', 'pessoas', 'espera-do-vinculo', pessoaId, empresaId] as const

/** A chave das conversas do `comunicacao` (`CHAVE_DAS_CONVERSAS`), escrita aqui sem o import. */
const CHAVE_DAS_CONVERSAS = ['comunicacao', 'conversas'] as const

/**
 * Espera o `cadastro` vincular o contato à Empresa definida. Desligado (`null` em qualquer dos
 * dois), não lê nada e responde `undefined`.
 *
 * **O teto é da própria espera** (code review da 13.20b2, B3): cada passo do relógio dela é uma
 * tentativa — com sucesso ou com erro —, contada num contador próprio; as leituras que
 * invalidações de fora disparam (a releitura de `['cadastro', 'pessoas']` depois de uma edição)
 * não a gastam. "Demorando" só com o teto esgotado; com erro persistente a espera também para no
 * teto. "Conferir de novo" zera o contador.
 */
export function useEsperaDoVinculo(
  pessoaId: string | null | undefined,
  empresaId: string | null | undefined,
): { estado: EstadoDoVinculo | undefined; conferirDeNovo: () => void } {
  const clienteDeConsultas = useQueryClient()
  const ligada = typeof pessoaId === 'string' && typeof empresaId === 'string'
  const chave = chaveDaEsperaDoVinculo(pessoaId ?? '', empresaId ?? '')
  const chaveEmTexto = chave.join('|')
  const consulta = useQuery({
    queryKey: chave,
    queryFn: async () => {
      const [pessoa] = await lerPessoasPorIds([pessoaId as string])
      return pessoa !== undefined && estaNaEmpresa(pessoa, empresaId as string)
    },
    enabled: ligada,
    retry: false,
    staleTime: 0,
  })
  const vinculado = consulta.data === true
  /* As tentativas da espera, presas à chave: outra Pessoa ou outra Empresa começa do zero. */
  const [tentativas, setTentativas] = useState({ chave: chaveEmTexto, feitas: 0 })
  const feitas = tentativas.chave === chaveEmTexto ? tentativas.feitas : 0
  const esgotada = feitas >= ESPERA_DO_VINCULO.tentativas
  const { refetch } = consulta
  useEffect(() => {
    if (!ligada || vinculado || esgotada) {
      return
    }
    const relogio = window.setTimeout(() => {
      void refetch().finally(() => {
        setTentativas((atual) => ({
          chave: chaveEmTexto,
          feitas: (atual.chave === chaveEmTexto ? atual.feitas : 0) + 1,
        }))
      })
    }, ESPERA_DO_VINCULO.intervaloEmMs)
    return () => {
      window.clearTimeout(relogio)
    }
  }, [ligada, vinculado, esgotada, feitas, refetch, chaveEmTexto])
  /* Vinculado, as listas de Pessoas, as fichas de Empresa e as conversas são relidas (a réplica do
     `comunicacao` passa a mostrar a Empresa na conversa). */
  useEffect(() => {
    if (vinculado) {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'pessoas'] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'empresa'] })
      void clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_CONVERSAS })
    }
  }, [vinculado, clienteDeConsultas])
  const conferirDeNovo = useCallback(() => {
    setTentativas({ chave: chaveEmTexto, feitas: 0 })
    void refetch()
  }, [chaveEmTexto, refetch])
  if (!ligada) {
    return { estado: undefined, conferirDeNovo }
  }
  const estado: EstadoDoVinculo = vinculado ? 'vinculado' : esgotada ? 'demorando' : 'vinculando'
  return { estado, conferirDeNovo }
}
